import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Star, Instagram } from "lucide-react";
import { SubtleWaves } from "@/components/ui/waves";

export const Reviews = () => {
  const [currentReview, setCurrentReview] = useState(0);

  const reviews = [
    {
      name: "Emma van der Berg",
      rating: 5,
      text: "Elke avond is nu een ritueel van rust. Lunssence heeft mijn leven echt veranderd – ik voel me zo veel meer gebalanceerd en in harmonie met mezelf.",
      location: "Amsterdam",
      verified: true,
    },
    {
      name: "Sophie Vermeulen",
      rating: 5,
      text: "Mijn huid straalt en ik voel me lichter. Deze thee heeft echt iets magisch – mijn hele uitstraling is veranderd sinds ik Lunssence drink.",
      location: "Rotterdam",
      verified: true,
    },
    {
      name: "Noor Al-Hassan",
      rating: 5,
      text: "De Signature Blend helpt me bij meditatie zoals niets anders. Het is alsof elke slok me dieper in mezelf brengt. Pure rust en verbinding.",
      location: "Utrecht", 
      verified: true,
    },
    {
      name: "Lisa Janssen",
      rating: 5,
      text: "Als iemand die veel stress heeft, was ik op zoek naar een natuurlijke oplossing. Deze blauwe lotus thee werkt echt! Ik slaap beter en voel me meer gebalanceerd.",
      location: "Den Haag",
      verified: true,
    },
  ];

  const instagramPosts = [
    { id: 1, image: "/api/placeholder/300/300", likes: 234 },
    { id: 2, image: "/api/placeholder/300/300", likes: 189 },
    { id: 3, image: "/api/placeholder/300/300", likes: 312 },
    { id: 4, image: "/api/placeholder/300/300", likes: 156 },
    { id: 5, image: "/api/placeholder/300/300", likes: 278 },
    { id: 6, image: "/api/placeholder/300/300", likes: 203 },
  ];

  const nextReview = () => {
    setCurrentReview((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentReview((prev) => (prev === 0 ? reviews.length - 1 : prev - 1));
  };

  return (
    <section id="reviews" className="relative py-24 bg-gradient-to-b from-background to-muted/20 overflow-hidden">
      {/* Elegant Wave Effects */}
      <SubtleWaves className="opacity-40" />
      
      {/* Soft Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-lavender/5 via-background/50 to-gold/5"></div>
      
      {/* Floating Golden Elements */}
      <div className="absolute top-1/4 right-8 w-32 h-32 bg-gradient-gold rounded-full opacity-10 animate-floating blur-2xl"></div>
      <div className="absolute bottom-1/3 left-8 w-24 h-24 bg-gradient-lavender rounded-full opacity-15 animate-floating-delayed blur-xl"></div>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6 animate-on-scroll">
            Wat Onze Klanten Zeggen
          </h2>
          
          <div className="w-16 h-1 bg-gradient-gold mx-auto mb-8 animate-on-scroll [animation-delay:0.2s]" />
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto animate-on-scroll [animation-delay:0.4s]">
            Meer dan 500 tevreden klanten hebben hun leven getransformeerd met Lunssence
          </p>
        </div>

        {/* Reviews Carousel */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="relative">
            <Card className="p-8 bg-card hover:shadow-gold transition-all duration-500 card-3d animate-on-scroll">
              <div className="text-center">
                {/* Stars */}
                <div className="flex justify-center gap-1 mb-6">
                  {[...Array(reviews[currentReview].rating)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-gold text-gold" />
                  ))}
                </div>
                
                {/* Review Text */}
                <blockquote className="text-lg text-muted-foreground leading-relaxed mb-8 italic">
                  "{reviews[currentReview].text}"
                </blockquote>
                
                {/* Author */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-primary text-lg">
                    {reviews[currentReview].name}
                  </h4>
                  <p className="text-muted-foreground">
                    {reviews[currentReview].location}
                  </p>
                  {reviews[currentReview].verified && (
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-gold/10 rounded-full text-sm text-gold border border-gold/20">
                      <span className="w-2 h-2 bg-gold rounded-full"></span>
                      Geverifieerde aankoop
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Navigation Buttons */}
            <Button
              variant="outline"
              size="icon"
              onClick={prevReview}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 rounded-full border-gold/30 hover:border-gold hover:bg-gold/10"
            >
              <ChevronLeft className="w-5 h-5 text-gold" />
            </Button>
            
            <Button
              variant="outline"
              size="icon"
              onClick={nextReview}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 rounded-full border-gold/30 hover:border-gold hover:bg-gold/10"
            >
              <ChevronRight className="w-5 h-5 text-gold" />
            </Button>
          </div>

          {/* Review Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentReview(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentReview ? 'bg-gold scale-125' : 'bg-gold/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Instagram Feed */}
        <div className="animate-on-scroll [animation-delay:0.6s]">
          <div className="text-center mb-12 relative z-10">
            <div className="bg-card/90 backdrop-blur-md rounded-2xl p-8 border border-gold/30 mx-auto max-w-md">
              <h3 className="font-serif text-3xl font-bold text-primary mb-4">
                #LunssenceLife
              </h3>
              <p className="text-muted-foreground">
                Deel je Lunssence momenten en inspireer anderen
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
            {instagramPosts.map((post, index) => (
              <div
                key={post.id}
                className="group relative aspect-square overflow-hidden rounded-lg cursor-pointer animate-on-scroll"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-full h-full bg-gradient-to-br from-gold/20 to-primary/20 flex items-center justify-center">
                  <Instagram className="w-12 h-12 text-gold/60" />
                </div>
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="text-white text-center">
                    <Instagram className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm font-semibold">{post.likes} likes</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <a
              href="https://www.instagram.com/lunssence_herbal_tea?igsh=MWptbG85aGd3eXFvcQ=="
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="btn-secondary">
                <Instagram className="w-5 h-5 mr-2" />
                Volg @lunssence_official
              </Button>
            </a>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="max-w-4xl mx-auto mt-16 animate-on-scroll [animation-delay:0.8s]">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-gold mb-1">500+</div>
              <p className="text-sm text-muted-foreground">Tevreden Klanten</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-gold mb-1">4.9★</div>
              <p className="text-sm text-muted-foreground">Gemiddelde Score</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-gold mb-1">100%</div>
              <p className="text-sm text-muted-foreground">Natuurlijk</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-gold mb-1">24h</div>
              <p className="text-sm text-muted-foreground">Snelle Verzending</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};